import { useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { invalidateAcornRulesCache } from "@/lib/acornSubscribers";
import { useAcornRules } from "@/hooks/queries/useAcornRules";
import type { AcornRule, NewAcornRule } from "@/hooks/queries/useAcornRules";
import type { MoriEventName } from "@/lib/events";

const AWARDABLE_EVENTS: MoriEventName[] = [
  "memory.created",
  "challenge.completed",
  "collection.started",
  "collection.completed",
  "collection.ended",
  "collection.active_mode.started",
  "collection.active_mode.ended",
  "streak.updated",
  "onboarding.completed",
  "tribe.invite.accepted",
  "child.birthday.today",
  "subscription.upgraded",
];

const BLANK_NEW_RULE: NewAcornRule = {
  event_name: "memory.created",
  condition_key: "",
  condition_value: "",
  acorn_amount: 1,
  label: "",
};

export default function AcornRulesEditor() {
  const { rules, isLoading, refetch, saveRule, addRule } = useAcornRules();
  const [localEdits, setLocalEdits] = useState<Record<string, Partial<AcornRule>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [newRule, setNewRule] = useState<NewAcornRule>({ ...BLANK_NEW_RULE });
  const [adding, setAdding] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const updateRule = (id: string, patch: Partial<AcornRule>) => {
    setLocalEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const handleSaveRule = async (baseRule: AcornRule) => {
    const rule = { ...baseRule, ...localEdits[baseRule.id] };
    setSaving(rule.id);
    try {
      await saveRule(rule);
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[baseRule.id];
        return next;
      });
    } catch {
      // toast handled in hook
    }
    setSaving(null);
  };

  const handleAddRule = async () => {
    if (!newRule.label.trim()) {
      toast.error("Label is required");
      return;
    }
    setAdding(true);
    try {
      await addRule(newRule);
      setNewRule({ ...BLANK_NEW_RULE });
    } catch {
      // toast handled in hook
    }
    setAdding(false);
  };

  const handleRefreshCache = async () => {
    invalidateAcornRulesCache();
    const result = await refetch();
    setLastRefresh(new Date());
    if (result.isError) {
      toast.error("Failed to reload rules");
    } else {
      toast.success("Cache cleared and rules reloaded.");
    }
  };

  const minutesAgo = Math.floor((Date.now() - lastRefresh.getTime()) / 60000);

  const conditionLabel = (rule: AcornRule) => {
    if (!rule.condition_key) return "always";
    return `${rule.condition_key} = ${rule.condition_value}`;
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Acorn Rules</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            DB-driven award rules. Amount −1 means "use payload.acornReward" (for
            challenge/collection cards).
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>
            Rules last refreshed:{" "}
            {minutesAgo === 0 ? "just now" : `${minutesAgo}m ago`}
          </span>
          <button
            onClick={handleRefreshCache}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Refresh now
          </button>
        </div>
      </div>

      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-5">
        Changes take effect within 5 minutes for all users. "Refresh now" clears
        only your session's cache.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th className="pb-2 pr-4">Event</th>
              <th className="pb-2 pr-4">Condition</th>
              <th className="pb-2 pr-4 w-28">Acorns</th>
              <th className="pb-2 pr-4">Label</th>
              <th className="pb-2 pr-4 w-16">Active</th>
              <th className="pb-2 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rules.map((baseRule) => {
              const rule = { ...baseRule, ...localEdits[baseRule.id] };
              return (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="py-2.5 pr-4 font-mono text-xs text-gray-700">
                    {rule.event_name}
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-gray-500">
                    {conditionLabel(rule)}
                  </td>
                  <td className="py-2.5 pr-4">
                    {rule.acorn_amount === -1 ? (
                      <span className="text-xs text-gray-400 italic">
                        from card/collection
                      </span>
                    ) : (
                      <input
                        type="number"
                        min={0}
                        value={rule.acorn_amount}
                        onChange={(e) =>
                          updateRule(rule.id, {
                            acorn_amount: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-16 rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                      />
                    )}
                  </td>
                  <td className="py-2.5 pr-4">
                    <input
                      type="text"
                      value={rule.label}
                      onChange={(e) => updateRule(rule.id, { label: e.target.value })}
                      className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                    />
                  </td>
                  <td className="py-2.5 pr-4">
                    <Switch
                      checked={rule.active}
                      onCheckedChange={(v) => updateRule(rule.id, { active: v })}
                    />
                  </td>
                  <td className="py-2.5">
                    <button
                      onClick={() => void handleSaveRule(baseRule)}
                      disabled={saving === rule.id}
                      className="rounded-md bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                    >
                      {saving === rule.id ? "Saving…" : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add new rule */}
      <div className="mt-8 border-t border-gray-200 pt-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Add new rule</h2>
        <div className="grid grid-cols-6 gap-3 items-end">
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Event</label>
            <select
              value={newRule.event_name}
              onChange={(e) =>
                setNewRule((r) => ({
                  ...r,
                  event_name: e.target.value as MoriEventName,
                }))
              }
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
            >
              {AWARDABLE_EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {ev}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Condition key
            </label>
            <input
              type="text"
              placeholder="e.g. isFirstToday"
              value={newRule.condition_key}
              onChange={(e) =>
                setNewRule((r) => ({ ...r, condition_key: e.target.value }))
              }
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Condition value
            </label>
            <input
              type="text"
              placeholder="e.g. true"
              value={newRule.condition_value}
              onChange={(e) =>
                setNewRule((r) => ({ ...r, condition_value: e.target.value }))
              }
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Acorns (−1 = from card)
            </label>
            <input
              type="number"
              min={-1}
              value={newRule.acorn_amount}
              onChange={(e) =>
                setNewRule((r) => ({
                  ...r,
                  acorn_amount: parseInt(e.target.value) || 0,
                }))
              }
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Label</label>
            <input
              type="text"
              placeholder="First memory today"
              value={newRule.label}
              onChange={(e) =>
                setNewRule((r) => ({ ...r, label: e.target.value }))
              }
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <div>
            <button
              onClick={() => void handleAddRule()}
              disabled={adding}
              className="w-full rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {adding ? "Adding…" : "Add rule"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
